import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  StackProps
} from "@chakra-ui/react"
import { flattenEdges, getIsLocalDev } from "@pathwright/utils"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { useSize } from "@pathwright/ux/src/utils/size"
import { useMemo } from "react"
import {
  UserSpaceMembershipFragment,
  useUserSpaceMembershipsQuery
} from "../../api/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { canAdministrate } from "../../user/permissions"
import SpaceAugmentationIdentity from "../augmentation/SpaceAugmentationIdentity"

function getSpaceLink(membership: UserSpaceMembershipFragment) {
  const { school } = membership
  // Let's simply reload the page to switch spaces. Better UX would be to
  // switch spaces without a full reload, but this is simpler and more
  // reliable for now.
  // When in local dev, we must supply the url as the ?school query param.
  if (getIsLocalDev()) {
    return `${window.location.origin}?school=${school.sso_link}`
  } else {
    return school.sso_link!
  }
}

function SpaceIdentity({
  space,
  ...stackProps
}: {
  space: UserSpaceMembershipFragment["school"]
} & StackProps) {
  return (
    <HStack {...stackProps} w="full">
      <SpaceAugmentationIdentity image={space.image!} name={space?.name} />
      <Heading as="h1" size="sm" m={0} noOfLines={1}>
        {space?.name}
      </Heading>
    </HStack>
  )
}

const SpaceSwitcher = () => {
  const [menuButtonRef, menuButtonWidth] = useSize("width")
  const pwContext = usePathwrightContext()
  const { school, me } = pwContext

  const userSpaceMembershipsQuery = useUserSpaceMembershipsQuery()
  const memberships = (flattenEdges(
    userSpaceMembershipsQuery?.data?.me?.memberships
  ) || []) as UserSpaceMembershipFragment[]

  // Sorting memberships based on when the user was last seen (based on heartbeat).
  const sortedMemberships = useMemo(() => {
    // Filter out current school and sort by last seen date descending.
    return memberships
      .filter((membership) => membership.school.id !== school.id)
      .sort((a, b) => {
        return (
          new Date(b.last_seen || 0).getTime() -
          new Date(a.last_seen || 0).getTime()
        )
      })
  }, [memberships])

  return me ? (
    <Menu colorScheme="blackAlpha" size="full">
      <Box ref={menuButtonRef} w="full">
        <MenuButton
          as={Button}
          variant="ghost"
          colorScheme="blackAlpha"
          size="md"
          rightIcon={<Pathicon icon="triangles" />}
          p=".5em"
          px=".75em"
          w="full"
        >
          <SpaceIdentity space={school} />
        </MenuButton>
      </Box>
      <MenuList maxH="200px" minW={`${menuButtonWidth}px`} overflowY="auto">
        {sortedMemberships.map((membership) => (
          <MenuItem
            as={Link}
            key={membership.school.id}
            variant="ghost"
            colorScheme="blackAlpha"
            borderRadius="none"
            href={getSpaceLink(membership)}
            target="_self"
          >
            <SpaceIdentity space={membership.school} />
          </MenuItem>
        ))}
        {canAdministrate(pwContext) && (
          <MenuItem
            as={Link}
            variant="ghost"
            colorScheme="blackAlpha"
            borderRadius="none"
            href={`https://paths.pathwright.com/sign-up/?utm_campaign=space-switcher&utm_medium=platform&utm_content=school-${school.subdomain}-${school.id}`}
          >
            <HStack w="full">
              <Flex align="center" justify="center" w="2rem" h="2rem">
                <Pathicon
                  icon="plus-circle-fill"
                  color="blackAlpha.700"
                  size="1.5rem"
                />
              </Flex>
              <Heading as="h2" size="sm">
                New Pathwright Space
              </Heading>
            </HStack>
          </MenuItem>
        )}
      </MenuList>
    </Menu>
  ) : (
    <SpaceIdentity space={school} p=".5em" px=".75em" />
  )
}

export default SpaceSwitcher
